import { useEffect, useRef } from 'react';
import { useNavermaps } from 'react-naver-maps';
import {
	BOTTOM_MODAL_SHEET_COLLAPSED_HEIGHT,
	BOTTOM_SHEET_TOP_MARGIN,
	NAVIGATION_HEIGHT,
} from '@/shared/consts';
import { useBoundStore } from '@/shared/store';
import { focusMapPosition } from '../lib';
import useNaverMapStore from './naverMapStore';

const KM_PER_DEGREE = 111;
const MAX_CONTAINER_WIDTH = 448;

const useMapFitRadius = (
	map: naver.maps.Map | null,
	radiusKm: number,
	latitude?: number,
	longitude?: number,
) => {
	const navermaps = useNavermaps();
	const isFirstRender = useRef(false);
	const currentSnapPoint = useBoundStore((store) => store.currentSnapPoint);
	const setMinZoomLevel = useNaverMapStore((state) => state.setMinZoomLevel);

	useEffect(() => {
		if (!map || !latitude || !longitude || isFirstRender.current) return;

		const containerWidth = Math.min(window.innerWidth, MAX_CONTAINER_WIDTH);
		const aspectRatio = window.innerHeight / containerWidth;

		const halfWidth = radiusKm / Math.sqrt(1 + aspectRatio ** 2);
		const halfHeight = halfWidth * aspectRatio;

		const lngOffset =
			halfWidth / (KM_PER_DEGREE * Math.cos((latitude * Math.PI) / 180));
		const latOffset = halfHeight / KM_PER_DEGREE;

		map.fitBounds(
			new navermaps.LatLngBounds(
				new navermaps.LatLng(latitude - latOffset, longitude - lngOffset),
				new navermaps.LatLng(latitude + latOffset, longitude + lngOffset),
			),
		);

		const currentZoom = map.getZoom();
		map.setZoom(currentZoom + 1);
		setMinZoomLevel(currentZoom + 1);

		window.requestAnimationFrame(() => {
			const mapElement = map.getElement?.() as HTMLElement | undefined;
			const mapRect = mapElement?.getBoundingClientRect();
			if (!mapRect) return;

			const sheetHeight =
				(currentSnapPoint ?? 0) >= 2
					? (window.innerHeight - BOTTOM_SHEET_TOP_MARGIN - NAVIGATION_HEIGHT) *
						0.5
					: BOTTOM_MODAL_SHEET_COLLAPSED_HEIGHT;
			const sheetTopFromScreenTop =
				window.innerHeight - NAVIGATION_HEIGHT - sheetHeight;
			const sheetTopWithinMap = Math.min(
				mapRect.height,
				Math.max(0, sheetTopFromScreenTop - mapRect.top),
			);

			focusMapPosition({
				map,
				position: new navermaps.LatLng(latitude, longitude),
				targetY: sheetTopWithinMap / 2,
			});
		});

		isFirstRender.current = true;
	}, [
		latitude,
		longitude,
		radiusKm,
		navermaps,
		map,
		setMinZoomLevel,
		currentSnapPoint,
	]);
};

export default useMapFitRadius;
